import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {CpConditionSetSummary} from '../../interface/cp-condition-set-summary.interface';
import {CpConditionSetUsage} from '../../interface/cp-condition-set-usage.interface';
import {CpConditionSetOverview} from '../../interface/cp-condition-set-overview.interface';
import {ProvisionAssignmentConditionMode} from '../../enum/provision-assignment-condition-mode.enum';
import {cpConditionModeOf} from '../domain/cp-provision-assignment-columns';

/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: DataProvider
 * Reason: QL helpers of the assignment-condition redesign. The 13 contract dimensions moved from the
 *     six assignment entities to cp.ProvisionAssignmentCondition rows that belong to a named
 *     cp.ProvisionAssignmentConditionSet; the assignments only reference a set. This service answers
 *     the three questions the UI needs: the conditions of one set (with dimension names), the sets of
 *     a provision type with their condition counts, and which assignments of the six families
 *     reference a set.
 */

/** ObjectType aliases of the six assignment families that carry `provisionAssignmentConditionSet`. */
export const CP_ASSIGNMENT_ENTITIES: { entity: string; label: string }[] = [
    {entity: 'cp.ProvisionTypeContractEligibility', label: 'Relevanz-Zuordnung'},
    {entity: 'cp.ProvisionTypeContractProvisionBase', label: 'Provisionsbasis-Zuordnung'},
    {entity: 'cp.ProvisionTypeContractLiability', label: 'Haftungs-Zuordnung'},
    {entity: 'cp.ProvisionTypeMatrixAssignment', label: 'Matrix-Zuordnung'},
    {entity: 'cp.ProvisionTypeCalculatedAgent', label: 'Berechneter Vermittler'},
    {entity: 'cp.ProvisionTypeComponentEligibility', label: 'Komponenten-Relevanz'}
];

/**
 * Join aliases of the conditions query: relation name -> alias. The name column of a dimension is
 * `<alias>.name` in the result, e.g. `g.name`, `prod.name`.
 */
export const CP_CONDITION_DIMENSION_JOINS: { relation: string; alias: string }[] = [
    {relation: 'group', alias: 'g'},
    {relation: 'product', alias: 'prod'},
    {relation: 'partner', alias: 'pa'},
    {relation: 'contractClosureChannelType', alias: 'cct'},
    {relation: 'contractOriginType', alias: 'cot'},
    {relation: 'contractBaseCategory', alias: 'cbc'},
    {relation: 'contractBaseType', alias: 'cbt'},
    {relation: 'category', alias: 'cat'},
    {relation: 'contractType', alias: 'ct'},
    {relation: 'formerContractPartner', alias: 'fpa'}
];

/** Alias of the QL pipe of {@link CpProvisionAssignmentConditionService.buildConditionsOfSetQuery}. */
export const CP_CONDITIONS_OF_SET_QUERY_NAME = 'cp.conditionSet.conditions';
/** Alias of the QL pipe of {@link CpProvisionAssignmentConditionService.buildAssignmentsWithSetQuery}. */
export const CP_ASSIGNMENTS_WITH_SET_QUERY_NAME = 'cp.assignment.withSet';

@Injectable({providedIn: 'root'})
export class CpProvisionAssignmentConditionService {

    constructor(private qlService: QlService) {
    }

    /**
     * Widget query: the conditions of ONE set ordered by `orderPriority`, dimension names joined
     * (left joins, `<alias>.name`, see {@link CP_CONDITION_DIMENSION_JOINS}). Use with
     * `WidgetFactory.createWidgetTableQl(alias, title, query, ...)`. Column paths: `e.orderPriority`,
     * `e.conditionMode`, `e.termMonthsFrom`, `e.termMonthsTo`, `e.formerContractRequirement`,
     * `e.formerTermMonthsFrom`, `e.formerTermMonthsTo`, `e.description` and `<alias>.name`.
     */
    buildConditionsOfSetQuery(conditionSetId: number): QlQueryDto {
        return {
            name: CP_CONDITIONS_OF_SET_QUERY_NAME,
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionAssignmentCondition',
                as: 'e',
                joins: CP_CONDITION_DIMENSION_JOINS.map(join => ({
                    type: 'entity' as const,
                    name: join.relation,
                    as: join.alias,
                    joinType: 'left' as const,
                    fields: [{name: 'name'}]
                }))
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionAssignmentConditionSetId',
                    FilterCriteria.cOperatorEqual, conditionSetId)
            ],
            sortings: [
                new Sorting('e.orderPriority', true),
                new Sorting('e.id', true)
            ]
        };
    }

    /**
     * Widget query: the assignments of ONE family and provision type joined with their condition
     * set (`cs.name`, `cs.alias`). The number of conditions is NOT part of it (QL cannot count a
     * reverse relation in a `view`); combine with {@link getSetOverview} on the component side.
     *
     * @param entity          one of {@link CP_ASSIGNMENT_ENTITIES}
     * @param provisionTypeId owning provision type
     */
    buildAssignmentsWithSetQuery(entity: string, provisionTypeId: number): QlQueryDto {
        return {
            name: CP_ASSIGNMENTS_WITH_SET_QUERY_NAME,
            type: 'view',
            start: {
                type: 'entity',
                name: entity,
                as: 'e',
                joins: [{
                    type: 'entity',
                    name: 'provisionAssignmentConditionSet',
                    as: 'cs',
                    joinType: 'left',
                    fields: [{name: 'name'}, {name: 'alias'}]
                }]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual, provisionTypeId)
            ],
            sortings: [
                new Sorting('e.orderPriority', true),
                new Sorting('e.id', true)
            ]
        };
    }

    /**
     * Counts and usages of every condition set of a provision type — ONE batched QL request with
     * eight view queries: the sets, all conditions of the type (counted client side into total /
     * INCLUDE / EXCLUDE — configuration data, so the row counts are small) and one query per
     * assignment family for the rows that reference a set ("Verwendung"). Sets without conditions
     * get 0; assignment rows without a set are skipped.
     *
     * A failed request is PROPAGATED as an error, never mapped to an empty result: an empty answer
     * would read as "0 Bedingungen" / "nicht verwendet", i.e. a wrong statement about the
     * configuration. The caller shows an error state with a retry instead.
     */
    getSetOverview(provisionTypeId: number): Observable<CpConditionSetOverview> {

        if (!provisionTypeId) {
            return of({summaries: [], usages: []});
        }

        return this.qlService.builder()
            .showErrors(false)
            .query({
                name: 'cpConditionSets',
                type: 'view',
                start: {
                    type: 'entity', name: 'cp.ProvisionAssignmentConditionSet', as: 'e',
                    fields: [{name: 'id'}, {name: 'name'}, {name: 'alias'}]
                },
                filters: [FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual, provisionTypeId)],
                sortings: [new Sorting('e.name', true), new Sorting('e.id', true)]
            })
            .query({
                name: 'cpConditionsOfType',
                type: 'view',
                start: {
                    type: 'entity', name: 'cp.ProvisionAssignmentCondition', as: 'e',
                    fields: [{name: 'id'}, {name: 'provisionAssignmentConditionSetId'}, {name: 'conditionMode'}]
                },
                filters: [FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual, provisionTypeId)]
            })
            .queries(...CP_ASSIGNMENT_ENTITIES.map((family, index): QlQueryDto => ({
                name: 'cpSetUsage' + index,
                type: 'view',
                start: {
                    type: 'entity', name: family.entity, as: 'e',
                    fields: [{name: 'id'}, {name: 'name'}, {name: 'alias'}, {name: 'provisionAssignmentConditionSetId'}]
                },
                filters: [FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual, provisionTypeId)]
            })))
            .execute()
            .pipe(
                map(result => ({
                    summaries: this.toSummaries(result.get('cpConditionSets') ?? [],
                        result.get('cpConditionsOfType') ?? []),
                    usages: this.toUsages(index => result.get('cpSetUsage' + index) ?? [])
                }))
            );
    }

    /** Sets joined with their client-side counted conditions (total / INCLUDE / EXCLUDE). */
    private toSummaries(setRows: any[], conditionRows: any[]): CpConditionSetSummary[] {

        const counts: { [setId: number]: { total: number; exclude: number } } = {};
        for (const row of conditionRows) {
            const setId = row?.['e.provisionAssignmentConditionSetId'];
            if (setId === null || setId === undefined) {
                continue;
            }
            const bucket = counts[+setId] ?? (counts[+setId] = {total: 0, exclude: 0});
            bucket.total++;
            if (cpConditionModeOf(row?.['e.conditionMode']) === ProvisionAssignmentConditionMode.EXCLUDE) {
                bucket.exclude++;
            }
        }

        return setRows
            .filter(row => row?.['e.id'] !== null && row?.['e.id'] !== undefined)
            .map(row => {
                const bucket = counts[+row['e.id']] ?? {total: 0, exclude: 0};
                return {
                    id: +row['e.id'],
                    name: row['e.name'] ?? '',
                    alias: row['e.alias'] ?? '',
                    conditionCount: bucket.total,
                    includeCount: bucket.total - bucket.exclude,
                    excludeCount: bucket.exclude
                } as CpConditionSetSummary;
            });
    }

    /** The assignment rows of the six families that reference a set; rows without a set are skipped. */
    private toUsages(rowsOfFamily: (index: number) => any[]): CpConditionSetUsage[] {

        const usages: CpConditionSetUsage[] = [];
        CP_ASSIGNMENT_ENTITIES.forEach((family, index) => {
            for (const row of rowsOfFamily(index)) {
                const setId = row?.['e.provisionAssignmentConditionSetId'];
                if (setId === null || setId === undefined) {
                    continue;
                }
                usages.push({
                    setId: +setId,
                    entity: family.entity,
                    entityLabel: family.label,
                    assignmentId: +row['e.id'],
                    name: row['e.name'] ?? '',
                    alias: row['e.alias'] ?? ''
                });
            }
        });
        return usages;
    }
}
